import { supabase } from './supabase'

export interface AdminStats {
  totalUsers: number
  totalAdmins: number
  newUsersWeek: number
  newUsersMonth: number
  totalContributions: number
  newContributionsWeek: number
  totalBrands: number
  usersWithContributions: number
  totalSaves: number
  allTags: string[]
}

export interface RecentActivity {
  activity_type: string
  user_email: string
  user_name: string
  details: string
  created_at: string
  contribution_id?: string
  comment?: string
}

export interface TopContributor {
  user_id: string
  user_email: string
  user_name: string
  username?: string
  contribution_count: number
  saves_received: number
  items_saved: number
}

class AdminService {
  async checkAdminStatus(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error checking admin status:', error)
        return false
      }

      return data?.is_admin || false
    } catch (err) {
      console.error('Error checking admin status:', err)
      return false
    }
  }

  async getAdminStatistics(): Promise<AdminStats | null> {
    try {
      const { data, error } = await supabase
        .from('admin_statistics')
        .select('*')
        .single()

      if (error) {
        console.error('Error fetching admin statistics:', error)
        console.error('This likely means the admin_statistics view needs to be created. Run the add_admin_permissions.sql migration.')
        return null
      }

      console.log('Admin statistics data:', data)

      return {
        totalUsers: data.total_users || 0,
        totalAdmins: data.total_admins || 0,
        newUsersWeek: data.new_users_week || 0,
        newUsersMonth: data.new_users_month || 0,
        totalContributions: data.total_contributions || 0,
        newContributionsWeek: data.new_contributions_week || 0,
        totalBrands: data.total_brands || 0,
        usersWithContributions: data.users_with_contributions || 0,
        totalSaves: data.total_saves || 0,
        allTags: data.all_tags || []
      }
    } catch (err) {
      console.error('Error fetching admin statistics:', err)
      return null
    }
  }

  async getRecentActivity(limit: number = 10): Promise<RecentActivity[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_recent_activity', { limit_count: limit })

      if (error) {
        console.error('Error fetching recent activity:', error)
        console.error('This likely means the get_recent_activity function needs to be created. Run the add_admin_permissions.sql migration.')
        return []
      }

      console.log('Recent activity data:', data)
      return data || []
    } catch (err) {
      console.error('Error fetching recent activity:', err)
      return []
    }
  }

  async getTopContributors(limit: number = 10): Promise<TopContributor[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_top_contributors', { limit_count: limit })

      if (error) {
        console.error('Error fetching top contributors:', error)
        console.error('This likely means the get_top_contributors function needs to be created. Run the add_admin_permissions.sql migration.')
        return []
      }

      console.log('Top contributors data:', data)
      return data || []
    } catch (err) {
      console.error('Error fetching top contributors:', err)
      return []
    }
  }

  async getAllUsers() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching users:', error)
        return []
      }

      return data || []
    } catch (err) {
      console.error('Error fetching users:', err)
      return []
    }
  }

  async toggleUserAdminStatus(userId: string, isAdmin: boolean) {
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_admin: isAdmin })
        .eq('id', userId)

      if (error) {
        console.error('Error updating admin status:', error)
        return false
      }

      return true
    } catch (err) {
      console.error('Error updating admin status:', err)
      return false
    }
  }

  async toggleUserFounderStatus(userId: string, isFounder: boolean) {
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_founder: isFounder })
        .eq('id', userId)

      if (error) {
        console.error('Error updating founder status:', error)
        return false
      }

      return true
    } catch (err) {
      console.error('Error updating founder status:', err)
      return false
    }
  }

  async getBrandStatistics() {
    try {
      const { data, error } = await supabase
        .from('contributions')
        .select('brand')
        .not('brand', 'is', null)

      if (error) {
        console.error('Error fetching brand statistics:', error)
        return []
      }

      // Group by brand and count
      const brandMap = new Map<string, number>()
      data.forEach(item => {
        if (item.brand) {
          brandMap.set(item.brand, (brandMap.get(item.brand) || 0) + 1)
        }
      })

      // Convert to sorted array
      const brands = Array.from(brandMap.entries())
        .map(([brand, count]) => ({ brand, count }))
        .sort((a, b) => b.count - a.count)

      return brands
    } catch (err) {
      console.error('Error fetching brand statistics:', err)
      return []
    }
  }

  async getTagStatistics() {
    try {
      // Use the new tagging system with item_tags junction table
      const { data, error } = await supabase
        .from('item_tags')
        .select(`
          tag_id,
          tags (
            id,
            name
          )
        `)

      if (error) {
        console.error('Error fetching tag statistics:', error)
        return []
      }

      // Count tags
      const tagMap = new Map<string, number>()
      data.forEach(item => {
        // item.tags is the joined tags table record (single object, not array)
        if (item.tags && typeof item.tags === 'object' && 'name' in item.tags) {
          const tagName = (item.tags as any).name
          tagMap.set(tagName, (tagMap.get(tagName) || 0) + 1)
        }
      })

      // Convert to sorted array
      const tags = Array.from(tagMap.entries())
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count)

      return tags
    } catch (err) {
      console.error('Error fetching tag statistics:', err)
      return []
    }
  }
}

export const adminService = new AdminService()